<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Chart, type ChartConfiguration, type ChartDataset, type TooltipItem } from 'chart.js/auto'
import { sideConfig } from '@/lib/product'
import { INSIGHTS_SIDES, formatDelta, sideColor, sideConfigKey, type InsightsCompetitors } from '@/lib/insights'

/**
 * Each competitor as a dot: x = vs market, y = its listings (ADR-027). A larger
 * marker per side places the customer on the same axes; x = 0 is the market.
 */
const props = defineProps<{
    data: InsightsCompetitors
}>()

const { t, locale } = useI18n()

const canvas = ref<HTMLCanvasElement | null>(null)
let instance: Chart | null = null

const GRID = 'rgba(156, 163, 175, 0.2)'
const AXIS = 'rgba(156, 163, 175, 0.7)'
const TICKS = 'rgba(156, 163, 175, 1)'

type Dot = { x: number; y: number; name: string }

const datasets = (): ChartDataset<'scatter', Dot[]>[] =>
    INSIGHTS_SIDES.flatMap((side) => {
        const rows = props.data[side]

        if (rows === null) {
            return []
        }

        const label = sideConfig[sideConfigKey(side)].label
        const sets: ChartDataset<'scatter', Dot[]>[] = [
            {
                label,
                data: rows.competitors.flatMap((competitor) =>
                    competitor.vs_market_pct === null
                        ? []
                        : [
                              {
                                  x: competitor.vs_market_pct,
                                  y: competitor.listings,
                                  name: `${competitor.name} ${competitor.country.toUpperCase()}`,
                              },
                          ],
                ),
                backgroundColor: sideColor(side, 0.75),
                borderColor: sideColor(side),
                pointRadius: 5,
                pointHoverRadius: 7,
            },
        ]

        if (rows.you !== null && rows.you.vs_market_pct !== null) {
            sets.push({
                label: `${t('insights.competitors.you')} · ${label}`,
                data: [{ x: rows.you.vs_market_pct, y: rows.you.listings, name: t('insights.competitors.you') }],
                backgroundColor: sideColor(side, 0.15),
                borderColor: sideColor(side),
                borderWidth: 2,
                pointStyle: 'rectRot',
                pointRadius: 9,
                pointHoverRadius: 11,
            })
        }

        return sets
    })

const render = () => {
    if (canvas.value === null) {
        return
    }

    if (instance !== null) {
        instance.data.datasets = datasets()
        instance.update()

        return
    }

    const config: ChartConfiguration<'scatter', Dot[]> = {
        type: 'scatter',
        data: { datasets: datasets() },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'top',
                    align: 'start',
                    labels: { color: TICKS, usePointStyle: true },
                },
                tooltip: {
                    callbacks: {
                        label: (item: TooltipItem<'scatter'>) => {
                            const dot = item.raw as Dot

                            return `${dot.name}: ${formatDelta(dot.x)} · ${Math.round(dot.y).toLocaleString(locale.value)}`
                        },
                    },
                },
            },
            scales: {
                x: {
                    title: { display: true, text: t('insights.competitors.scatterX'), color: TICKS },
                    border: { display: false },
                    grid: {
                        color: (context) => (context.tick.value === 0 ? AXIS : GRID),
                        lineWidth: (context) => (context.tick.value === 0 ? 1.5 : 1),
                    },
                    ticks: {
                        color: TICKS,
                        callback: (value: string | number) => `${Number(value) > 0 ? '+' : ''}${value}%`,
                    },
                },
                y: {
                    title: { display: true, text: t('insights.competitors.scatterY'), color: TICKS },
                    beginAtZero: true,
                    border: { display: false },
                    grid: { color: GRID },
                    ticks: { color: TICKS },
                },
            },
        },
    }

    instance = new Chart(canvas.value, config)
}

onMounted(render)

watch(() => props.data, render)

onBeforeUnmount(() => {
    instance?.destroy()
    instance = null
})
</script>

<template>
    <div class="relative h-72">
        <canvas ref="canvas" />
    </div>
</template>
